import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import ScrollToAnchor from '~/src/components/scroll-to-anchor';
import icons from '../../images/desktop-icons.png';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <ScrollToAnchor mdxType="ScrollToAnchor" />
    <p>{`These are the foundational design concepts and constraints that can help inform design decisions. The core considerations for GitHub Desktop include platform differences and relationships to Primer Web guidelines.`}</p>
    <h2>{`Color`}</h2>
    <p>{`The color scheme file is the only element inherited directly from Primer CSS. For the most part, Desktop uses the same palette as Primer CSS, with a few exceptions:`}</p>
    <ul>
      <li parentName="ul">{`Use blue for the primary buttons (rather than the green buttons on github.com)`}</li>
      <li parentName="ul">{`Use blue for tab underlines (orange is used on github.com)`}</li>
    </ul>
    <h3>{`Dark mode`}</h3>
    <p>{`GitHub Desktop has a dark mode users can choose in their app preferences. It's displayed using a theme that essentially converts all the elements to their dark mode equivalents.`}</p>
    <h2>{`Iconography`}</h2>

    <p>{`GitHub Desktop uses `}<a parentName="p" {...{
        "href": "https://primer.style/octicons/"
      }}>{`octicons`}</a>{`, but doesn't inherit updates, instead they're duplicated into the Desktop code base. There are icons that are unique to Desktop:`}</p>
    <ul>
      <li parentName="ul">{`Stash: Uses the octicon for ‘modified’ but made to appear stacked`}</li>
      <li parentName="ul">{`Commit co-author: Person with plus sign`}</li>
      <li parentName="ul">{`Avatar default: The old version (a filled in silhouette with rounded container) is used instead of the newer outlined style due to the context`}</li>
      <li parentName="ul">{`Force push icon: Push arrow with outline of two more arrows to make it look more extreme`}</li>
      <li parentName="ul">{`Repo force push: Repo with arrow with triple outline above`}</li>
    </ul>
    <img src={icons} alt="" />
    <p>{`Some older octicons have also been used in the illustrations (check mark, question mark, ufo with speech bubble with telescope, planet with thumbs up).`}</p>
    <h2>{`Illustrations`}</h2>
    <p>{`GitHub Desktop has its own set of illustrations, which were adopted from a previous GitHub Universe with a space theme. Illustrations primarily show up in the welcome wizard and empty states for lists areas. Because Desktop has a dark mode, hard-coded colors need to look good on either white or dark gray backgrounds. They are composed of 1 or 2 swatches that get swapped out with a css invert filter on. If a shadow is included in the illustration, it needs to be translucent.`}</p>
    <p>{`All of the illustrations can be found in the `}<a parentName="p" {...{
        "href": "https://github.com/desktop/desktop/tree/development/app/static/common"
      }}>{`desktop repo`}</a>{`.`}</p>
    <h2>{`Platforms`}</h2>
    <p>{`Desktop is built using Electron, which allows it to be cross-platform. With Electron, it’s possible to implement anything that can be implemented on github.com, and more. For example, in the future, multiple desktop windows could be part of the app experience. Though Electron enables a cross-platform app, there are a few key differentiating features between macOS and Windows that need to be considered when designing for GitHub Desktop.`}</p>
    <h3>{`Button order`}</h3>
    <h4>{`Button order on macOS`}</h4>
    <p>{`The secondary button is always on the left, whether it’s the default or cancel, and the primary action is on the right. This is aligned with the Primer guidelines used on github.com.`}</p>
    <p>{`Example: "Cancel | Let’s Do This"`}</p>
    <h4>{`Button order on Windows`}</h4>
    <p>{`The default button is always on the far right and the primary action is on the left. This differs from the Primer guidelines and should be taken into consideration for Windows users.`}</p>
    <p>{`Example: "Let’s do this | Cancel"`}</p>
    <h3>{`Capitalization`}</h3>
    <h4>{`Capitalization on macOS`}</h4>
    <p>{`Title case is used for headlines and buttons. This is different from Primer guidelines, so it needs to be taken into consideration.`}</p>
    <p>{`Example: "Current Repository"`}</p>
    <h4>{`Capitalization on Windows`}</h4>
    <p>{`Sentence case is used for headlines and buttons, which is aligned with Primer guidelines.`}</p>
    <p>{`Example: "Current repository"`}</p>
    <h3>{`Menu bar`}</h3>
    <h4>{`Menu bar on macOS`}</h4>
    <p>{`The menu bar on macOS sits at the top of the screen, disconnected from the GitHub Desktop window. The system menu bar is used.`}</p>
    <h4>{`Menu bar on Windows`}</h4>
    <p>{`The menu bar on Windows sits directly on top of the GitHub Desktop UI, with no separation or ability to disconnect it. The menu bar is a custom design, so when considering changing elements in the topbar (the top of the Desktop interface), consider the Windows menu bar in relation.`}</p>
    <h2>{`Relationship to Primer Web`}</h2>
    <p>{`GitHub Desktop's architecture and Primer Web are not compatible. For that reason, Desktop has its own set of components and CSS that leverage Primer Web, and doesn't take advantage of Primer updates to the code directly, with the exception of the color scheme file.`}</p>
    <h2>{`Spacing`}</h2>
    <p>{`GitHub Desktop uses a 10 pixel grid (different from the 8px grid used by Primer). Changing the spacing scale has been considered and is documented in `}<a parentName="p" {...{
        "href": "https://github.com/desktop/desktop/compare/spacing-scale-spike-v2"
      }}>{`this PR`}</a>{`.`}</p>
    <h2>{`System Elements`}</h2>
    <p>{`The following UI elements use system-generated code that has not been customized or styled:`}</p>
    <ul>
      <li parentName="ul">{`Context menus`}</li>
      <li parentName="ul">{`Tooltips`}</li>
      <li parentName="ul">{`Dropdowns`}</li>
      <li parentName="ul">{`Menu bar on macOS (Windows uses a custom menu bar)`}</li>
    </ul>
    <h2>{`Typography`}</h2>
    <p>{`GitHub Desktop uses system fonts. When creating design mockups for GitHub Desktop, consider that:`}</p>
    <ul>
      <li parentName="ul">{`In Figma, use macOS fonts (Primer Web defaults) for mocking up designs, unless the feature is Windows-specific.`}</li>
      <li parentName="ul">{`Windows' system font is Segoe UI, and type styles for Windows are included in the Figma library to make it easy to switch from SF Pro to Segoe to show the difference between Mac and Windows screens. Segoe UI reads a little smaller and tighter, so it may be worth adjusting the point size to make it feel consistent with macOS.`}</li>
      <li parentName="ul">{`Styles and sizes follow the same guidelines as Primer Web, with the addition of `}<inlineCode parentName="li">{`text-x-small`}</inlineCode>{` (11pt and used for all small, secondary text).`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "https://aka.ms/SegoeFonts"
      }}>{`Download Segoe UI (Mac only)`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      